/**
 * Import Configuration Types
 * Types for managing Excel import configuration (sheet mappings and language column mappings)
 */

import { ApplicationType } from './translation-types';

// ============================================================================
// Core Interfaces
// ============================================================================

/**
 * Column mapping - maps a language code to an Excel column number
 */
export interface ColumnMappingItem {
  columnMappingId: number;
  sheetMappingId: number;
  languageCode: string;  // e.g., "en", "he", "de", "fr"
  columnNumber: number;  // Excel column number (1-based, A=1, B=2, etc.)
  isActive: boolean;
}

/**
 * Sheet mapping - maps an Excel sheet to an application type
 */
export interface SheetMappingItem {
  mappingId: number;
  sheetName: string;
  applicationType: ApplicationType;
  keyColumnNumber: number;          // Column number for translation key (usually 1)
  descriptionColumnNumber?: number; // Column number for description (usually 2)
  isActive: boolean;
  sortOrder: number;
  columnMappings: ColumnMappingItem[];
}

// ============================================================================
// Request Types
// ============================================================================

/**
 * Request to create or update a sheet mapping
 */
export interface SheetMappingRequest {
  mappingId?: number;  // null/undefined for create, id for update
  sheetName: string;
  applicationType: ApplicationType;
  keyColumnNumber: number;
  descriptionColumnNumber?: number;
}

/**
 * Request to create or update a column mapping
 */
export interface ColumnMappingRequest {
  columnMappingId?: number;  // null/undefined for create, id for update
  sheetMappingId: number;
  languageCode: string;
  columnNumber: number;
}

// ============================================================================
// Response Types
// ============================================================================

/**
 * Response for sheet mapping list
 */
export interface SheetMappingListResponse {
  isSuccess: boolean;
  errorMessage?: string;
  errorCode?: string;
  mappings: SheetMappingItem[];
}

/**
 * Response for single sheet mapping (with column mappings)
 */
export interface SheetMappingDetailResponse {
  isSuccess: boolean;
  errorMessage?: string;
  errorCode?: string;
  mapping: SheetMappingItem;
}

/**
 * Response for sheet mapping create/update
 */
export interface SheetMappingMutationResponse {
  isSuccess: boolean;
  errorMessage?: string;
  errorCode?: string;
  mapping?: SheetMappingItem;
}

/**
 * Response for sheet mapping delete
 */
export interface SheetMappingDeleteResponse {
  isSuccess: boolean;
  errorMessage?: string;
  errorCode?: string;
  deleted: boolean;
}

/**
 * Response for column mapping list
 */
export interface ColumnMappingListResponse {
  isSuccess: boolean;
  errorMessage?: string;
  errorCode?: string;
  columns: ColumnMappingItem[];
}

/**
 * Response for column mapping create/update
 */
export interface ColumnMappingMutationResponse {
  isSuccess: boolean;
  errorMessage?: string;
  errorCode?: string;
  column?: ColumnMappingItem;
}

/**
 * Response for column mapping delete
 */
export interface ColumnMappingDeleteResponse {
  isSuccess: boolean;
  errorMessage?: string;
  errorCode?: string;
  deleted: boolean;
}

// ============================================================================
// Store Types
// ============================================================================

/**
 * Import configuration store state and actions
 */
export interface ImportConfigurationStore {
  // Data
  sheetMappings: SheetMappingItem[];
  selectedSheetMapping: SheetMappingItem | null;
  expandedMappingId: number | null;

  // UI States
  isLoading: boolean;
  isLoadingDetail: boolean;
  isSubmitting: boolean;
  error: string | null;
  submitError: string | null;

  // Dialog states
  sheetFormDialogOpen: boolean;
  editingSheetMapping: SheetMappingItem | null;
  columnFormDialogOpen: boolean;
  editingColumnMapping: ColumnMappingItem | null;
  columnFormSheetId: number | null;
  deleteDialogOpen: boolean;
  itemToDelete: { type: 'sheet' | 'column'; id: number; name: string } | null;

  // Actions - Sheet Mappings
  fetchSheetMappings: () => Promise<void>;
  fetchSheetMappingDetail: (mappingId: number) => Promise<void>;
  saveSheetMapping: (data: SheetMappingRequest) => Promise<SheetMappingItem>;
  deleteSheetMapping: (mappingId: number) => Promise<void>;

  // Actions - Column Mappings
  saveColumnMapping: (data: ColumnMappingRequest) => Promise<ColumnMappingItem>;
  deleteColumnMapping: (columnId: number) => Promise<void>;

  // UI Actions
  openSheetFormDialog: (mapping?: SheetMappingItem) => void;
  closeSheetFormDialog: () => void;
  openColumnFormDialog: (sheetMappingId: number, column?: ColumnMappingItem) => void;
  closeColumnFormDialog: () => void;
  openDeleteDialog: (type: 'sheet' | 'column', id: number, name: string) => void;
  closeDeleteDialog: () => void;
  setExpandedMappingId: (mappingId: number | null) => void;
  clearSubmitError: () => void;
  reset: () => void;
}

// ============================================================================
// Helper Functions & Constants
// ============================================================================

/**
 * Convert Excel column number to letter (1 = A, 2 = B, etc.)
 */
export function columnNumberToLetter(columnNumber: number): string {
  let result = '';
  let num = columnNumber;
  while (num > 0) {
    const remainder = (num - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    num = Math.floor((num - 1) / 26);
  }
  return result;
}

/**
 * Convert Excel column letter to number (A = 1, B = 2, etc.)
 */
export function columnLetterToNumber(columnLetter: string): number {
  let result = 0;
  for (let i = 0; i < columnLetter.length; i++) {
    result = result * 26 + (columnLetter.toUpperCase().charCodeAt(i) - 64);
  }
  return result;
}

/**
 * Format column number for display (e.g., "3 (C)")
 */
export function formatColumnDisplay(columnNumber: number): string {
  return `${columnNumber} (${columnNumberToLetter(columnNumber)})`;
}

/**
 * Get column mapping summary for display (e.g., "en: C, he: D, de: E")
 */
export function getColumnMappingSummary(columnMappings: ColumnMappingItem[]): string {
  if (!columnMappings || columnMappings.length === 0) {
    return 'No languages configured';
  }

  const sorted = [...columnMappings].sort((a, b) => a.columnNumber - b.columnNumber);
  return sorted
    .filter(cm => cm.isActive)
    .map(cm => `${cm.languageCode}: ${columnNumberToLetter(cm.columnNumber)}`)
    .join(', ');
}

/**
 * Default values for new sheet mapping
 */
export const DEFAULT_SHEET_MAPPING: Partial<SheetMappingRequest> = {
  keyColumnNumber: 1,
  descriptionColumnNumber: 2,
};

/**
 * Validate sheet mapping before submission
 */
export function validateSheetMapping(
  data: SheetMappingRequest,
  existingMappings: SheetMappingItem[],
  editingId?: number
): string | null {
  if (!data.sheetName?.trim()) {
    return 'Sheet name is required';
  }

  if (!data.applicationType) {
    return 'Application type is required';
  }

  if (!data.keyColumnNumber || data.keyColumnNumber < 1) {
    return 'Key column must be at least 1';
  }

  if (data.descriptionColumnNumber !== undefined && data.descriptionColumnNumber < 1) {
    return 'Description column must be at least 1';
  }

  if (data.descriptionColumnNumber && data.descriptionColumnNumber === data.keyColumnNumber) {
    return 'Description column cannot be the same as key column';
  }

  // Check for duplicate sheet name + application type
  const duplicate = existingMappings.find(m =>
    m.sheetName.toLowerCase() === data.sheetName.toLowerCase() &&
    m.applicationType === data.applicationType &&
    m.mappingId !== editingId
  );

  if (duplicate) {
    return 'A mapping with this sheet name and application type already exists';
  }

  return null;
}

/**
 * Validate column mapping before submission
 */
export function validateColumnMapping(
  data: ColumnMappingRequest,
  existingColumns: ColumnMappingItem[],
  keyColumnNumber: number,
  descriptionColumnNumber?: number,
  editingId?: number
): string | null {
  if (!data.languageCode?.trim()) {
    return 'Language code is required';
  }

  if (!data.columnNumber || data.columnNumber < 1) {
    return 'Column number must be at least 1';
  }

  // Check if column number conflicts with key or description column
  if (data.columnNumber === keyColumnNumber) {
    return `Column ${data.columnNumber} is already used for the translation key`;
  }

  if (descriptionColumnNumber && data.columnNumber === descriptionColumnNumber) {
    return `Column ${data.columnNumber} is already used for the description`;
  }

  // Check for duplicate language code in this sheet
  const duplicateLanguage = existingColumns.find(c =>
    c.languageCode.toLowerCase() === data.languageCode.toLowerCase() &&
    c.columnMappingId !== editingId
  );

  if (duplicateLanguage) {
    return `Language "${data.languageCode}" is already mapped to column ${duplicateLanguage.columnNumber}`;
  }

  // Check for duplicate column number in this sheet
  const duplicateColumn = existingColumns.find(c =>
    c.columnNumber === data.columnNumber &&
    c.columnMappingId !== editingId
  );

  if (duplicateColumn) {
    return `Column ${data.columnNumber} is already mapped to language "${duplicateColumn.languageCode}"`;
  }

  return null;
}
